import React, { createContext, ReactNode, useContext, useReducer } from 'react';

import { GlobalContext } from '../../../store';

import { IProcessScores, processScores } from './BoundActionCreators/processScores';

export type TStringArrayFieldName =
  | 'appliedHandicaps'
  | 'gross'
  | 'handicaps'
  | 'nets'
  | 'originals'
  | 'pars'
  | 'stablefords';

type TStringArrayFields = Record<TStringArrayFieldName, string[]>;

export interface ISetStringArrayValueActionValue {
  field: TStringArrayFieldName;
  value: string[];
}

interface ISetStringArrayValueAction {
  type: 'setStringArrayValue';
  value: ISetStringArrayValueActionValue;
}

interface ISetTotalsAction {
  type: 'setTotals';
  value: {
    gross: number;
    net: number;
  };
}

type IActions = ISetStringArrayValueAction | ISetTotalsAction;

interface IRoundScoreSummaryState extends TStringArrayFields {
  totalGross: number;
  totalNet: number;
}

const initialState: IRoundScoreSummaryState = {
  appliedHandicaps: [],
  gross: [],
  handicaps: [],
  nets: [],
  originals: [],
  pars: [],
  stablefords: [],
  totalGross: 0,
  totalNet: 0
};

interface IDispatch {
  dispatch: (action: IActions) => void;
  actions: IBoundActionCreators;
}

const RoundScoreSummaryStateContext = createContext<IRoundScoreSummaryState | undefined>(undefined);
const RoundScoreSummaryDispatchContext = createContext<IDispatch | undefined>(undefined);

const RoundScoreSummaryReducer = (
  state: IRoundScoreSummaryState,
  action: IActions
): IRoundScoreSummaryState => {
  switch (action.type) {
    case 'setStringArrayValue': {
      return {
        ...state,
        [action.value.field]: action.value.value
      };
    }
    case 'setTotals': {
      return {
        ...state,
        totalGross: action.value.gross,
        totalNet: action.value.net
      };
    }
  }
};

interface TeamsProviderProps {
  children: ReactNode;
}

interface IBoundActionCreators {
  processScores: IProcessScores;
}

export type IStateDispatchActions<T> = (
  state: IRoundScoreSummaryState,
  dispatch: (action: IActions) => void
) => T;

const RoundScoreSummaryProvider = ({ children }: TeamsProviderProps) => {
  const [state, dispatch] = useReducer(RoundScoreSummaryReducer, initialState as never);

  const {} = useContext(GlobalContext);

  const actions: IBoundActionCreators = {
    processScores: processScores(state, dispatch)
  };

  return (
    <RoundScoreSummaryStateContext.Provider value={state}>
      <RoundScoreSummaryDispatchContext.Provider value={{ dispatch, actions }}>
        {children}
      </RoundScoreSummaryDispatchContext.Provider>
    </RoundScoreSummaryStateContext.Provider>
  );
};

const useRoundScoreSummaryState = () => {
  const context = useContext(RoundScoreSummaryStateContext);
  if (context === undefined) {
    throw new Error('useRoundScoreSummaryState must be used within a RoundScoreSummaryProvider');
  }
  return context;
};

const useRoundScoreSummaryDispatch = () => {
  const context = useContext(RoundScoreSummaryDispatchContext);
  if (context === undefined) {
    throw new Error('useRoundScoreSummaryDispatch must be used within a RoundScoreSummaryProvider');
  }
  return context;
};

export { RoundScoreSummaryProvider, useRoundScoreSummaryState, useRoundScoreSummaryDispatch };
